import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

type ToastKind = 'success' | 'error' | 'info' | 'warning';
interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const KIND_CLASS: Record<ToastKind, string> = {
  success: 'text-bg-success',
  error: 'text-bg-danger',
  info: 'text-bg-info',
  warning: 'text-bg-warning',
};

// Legacy toastr-style leading icon per level.
const KIND_ICON: Record<ToastKind, string> = {
  success: 'fa fa-check-circle',
  error: 'fa fa-times-circle',
  info: 'fa fa-info-circle',
  warning: 'fa fa-exclamation-triangle',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const remove = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      const id = nextId.current++;
      setToasts((t) => [...t, { id, kind, message }]);
      window.setTimeout(() => remove(id), kind === 'error' ? 7000 : 4000);
    },
    [remove],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (m) => push('success', m),
      error: (m) => push('error', m),
      info: (m) => push('info', m),
      warning: (m) => push('warning', m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="finkit-toast-container toast-container position-fixed top-0 end-0 p-3">
        {toasts.map((t) => (
          <div key={t.id} className={`toast show border-0 mb-2 ${KIND_CLASS[t.kind]}`} role="alert">
            <div className="d-flex align-items-center">
              <i className={`${KIND_ICON[t.kind]} fs-5 ms-3`} aria-hidden="true" />
              <div className="toast-body flex-grow-1">{t.message}</div>
              <button
                type="button"
                className={`btn-close me-2 m-auto${t.kind === 'warning' ? '' : ' btn-close-white'}`}
                aria-label="Close"
                onClick={() => remove(t.id)}
              />
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
